import { useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';

export interface PagedList<T> {
  /** Clamped current page (1-based), never exceeds pageCount. */
  page: number;
  /** Total number of pages (at least 1). */
  pageCount: number;
  /** Items belonging to the current page. */
  pageItems: T[];
  /** Navigate to a page, clamped into [1, pageCount]. */
  goToPage: (next: number) => void;
  /** Set the raw page state (callers reset to 1 on filter/search change). */
  setPage: (page: number) => void;
}

/**
 * Paginates an already-loaded array in memory.
 *
 * The full dataset is expected to be loaded by the caller's store; this hook only
 * slices it for display. The current page auto-clamps when the list shrinks (e.g.
 * after filtering), so no entries are skipped.
 *
 * Pass `controlledPage` to keep the raw page elsewhere, e.g. in the URL via `useUrlListState`.
 */
export function usePagedList<T>(
  items: T[],
  pageSize: number = DEFAULT_PAGE_SIZE,
  controlledPage?: [number, (page: number) => void],
): PagedList<T> {
  const [localPage, setLocalPage] = useState(1);
  const [page, setPage] = controlledPage ?? [localPage, setLocalPage];

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);

  const pageItems = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize],
  );

  const goToPage = (next: number) => setPage(Math.max(1, Math.min(pageCount, next)));

  return { page: safePage, pageCount, pageItems, goToPage, setPage };
}

export default usePagedList;
